import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from "@angular/core";
import { combineLatest, Observable, of, race, Subscription } from "rxjs";
import { filter, map, switchMap, take, tap } from "rxjs/operators";

import { v4 } from "uuid";

import exportedSass from "@hermes/aphrodite/design-system-tools";
import {
    AVAILABLE_SOON,
    EXCEPTIONAL_PRODUCT,
    Product,
    ProductTemplateType,
} from "@hermes/api-model-product";
import { Context } from "@hermes/app-core";
import {
    ConfigMessageError,
    ERROR_CART_MESSAGE,
    hasSizeVariant,
    isRetailOnlyStock,
} from "@hermes/fragments/product-utils";
import { AddToCartFacade } from "@hermes/states/add-to-cart";
import { ApplePayFacade, ApplepayStatus } from "@hermes/states/apple-pay";
import { BackInStockFacade } from "@hermes/states/back-in-stock";
import {
    BasketFacade,
    EcomError,
    SerializedBasket,
} from "@hermes/states/basket";
import {
    BACK_IN_STOCK,
    FeatureFlagFacade,
    REGISTER_YOUR_INTEREST,
} from "@hermes/states/flipper";
import {
    ApplePayPage,
    PaymentMethodsFacade,
    PROCESSING_CHANNEL_PRODUCT,
    ProcessingChannel,
} from "@hermes/states/payment-methods";
import { RegisterYourInterestFacade } from "@hermes/states/register-your-interest";
import { TrayFacade } from "@hermes/states/tray";
import { EcomErrorCodes } from "@hermes/utils-generic/constants";

import { BreakpointService } from "@hermes/utils-generic/services/user-interface";

/**
 * Component to manage call to action add-to-cart and apple-pay button
 */
@Component({
    selector: "h-call-to-action-purchase",
    templateUrl: "./call-to-action-purchase.component.html",
    styleUrls: ["./call-to-action-purchase.component.scss"],
})
export class CallToActionPurchaseComponent
    implements OnInit, OnDestroy, OnChanges
{
    /**
     * True if add to cart button is displayed
     */
    @Input()
    public addToCartButtonIsDisplayed = true;

    @Input()
    public product!: Product;

    @Input()
    public border = true;

    @Input()
    public positions = [0];

    @Input()
    public inSticky = true;

    @Input()
    public showApplePayButton = true;

    @Input()
    public isProductEngraving = false;

    @Input()
    public buttonsPosition: "row" | "column" = "column";

    @Input()
    public hideButtonsOnError = false;

    @Input()
    public showNotifyMe = true;

    @Input()
    public showRegisterYourInterest = true;

    /** apply transparent background if true*/
    @Input()
    public transparentBackground: boolean = false;

    @ViewChild("backInStockNotifyMeButton", {
        read: ElementRef,
    })
    public backInStockNotifyMeButton?: ElementRef<HTMLElement>;

    @ViewChild("registerYourInterestButton", {
        read: ElementRef,
    })
    public registerYourInterestButton?: ElementRef<HTMLElement>;

    @Output()
    public addToCart: EventEmitter<boolean> = new EventEmitter();

    public shouldDisplayApplePayCTA$?: Observable<boolean>;
    public shouldDisplayNotifyMeCTA$?: Observable<boolean>;
    public shouldDisplayRegisterYourInterestCTA$?: Observable<boolean>;
    public shouldDisplayAddToCartCTA$?: Observable<boolean>;
    /**
     * Manage error of add to cart
     */
    public productError$?: Observable<ConfigMessageError>;

    public addToCartButtonEnabled$?: Observable<boolean>;

    public processingChannelProduct: ProcessingChannel =
        PROCESSING_CHANNEL_PRODUCT;

    public isMobile$ = this.breakpointService
        .mediumBreakpointObserver()
        .pipe(map((value) => !value.matches));

    private subscription: Subscription = new Subscription();

    constructor(
        public paymentMethodsFacade: PaymentMethodsFacade,
        private applePayFacade: ApplePayFacade,
        private backInStockFacade: BackInStockFacade,
        private registerYourInterestFacade: RegisterYourInterestFacade,
        private basketFacade: BasketFacade,
        private addToCartFacade: AddToCartFacade,
        private trayFacade: TrayFacade,
        public cdr: ChangeDetectorRef,
        private context: Context,
        private featureFlagFacade: FeatureFlagFacade,
        private breakpointService: BreakpointService,
    ) {}

    public ngOnInit(): void {
        this.subscription.add(
            this.addingProductToCartOnApplePayButtonClick().subscribe(),
        );
        this.subscription.add(
            this.backInStockFacade.loading$
                .pipe(filter<boolean>((loading) => loading))
                .subscribe(() => this.askForBackInStockSubscription()),
        );
        this.subscription.add(
            this.registerYourInterestFacade.loading$
                .pipe(filter<boolean>((loading) => loading))
                .subscribe(() => this.askForRegisterYourInterestSubscription()),
        );

        if (this.context.isInBrowserMode()) {
            this.paymentMethodsFacade.fetchPaymentMethods({ cache: true });
        }

        this.addToCartButtonEnabled$ =
            this.addToCartFacade.isAddToCartButtonEnabled(this.positions);

        this.productError$ = this.addToCartFacade
            .selectAddToCartEcomError(this.positions)
            .pipe(
                map(({ errorCode, enabled }) => {
                    errorCode = this.getErrorCodeFromProduct(
                        errorCode as EcomErrorCodes,
                    );

                    const configMessageError =
                        ERROR_CART_MESSAGE.get(errorCode) ??
                        ERROR_CART_MESSAGE.get(
                            EcomErrorCodes.ERROR_CODE_CART_DEFAULT,
                        );
                    return {
                        ...configMessageError,
                        enabled,
                    } as ConfigMessageError;
                }),
            );
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes["product"]) {
            this.shouldDisplayNotifyMeCTA$ = this.featureFlagFacade
                .isActivated(BACK_IN_STOCK)
                .pipe(
                    map(
                        (backInStock) =>
                            backInStock &&
                            this.showNotifyMe &&
                            Boolean(this.product?.stock?.displayOnly) &&
                            this.product?.labelDisplayOnly === AVAILABLE_SOON &&
                            this.product.templateType ===
                                ProductTemplateType.Simple,
                    ),
                );
            this.shouldDisplayRegisterYourInterestCTA$ = this.featureFlagFacade
                .isActivated(REGISTER_YOUR_INTEREST)
                .pipe(
                    map(
                        (registerYourInterest) =>
                            registerYourInterest &&
                            this.showRegisterYourInterest &&
                            Boolean(this.product?.stock?.displayOnly) &&
                            this.product?.labelDisplayOnly ===
                                EXCEPTIONAL_PRODUCT &&
                            this.product.templateType ===
                                ProductTemplateType.Simple,
                    ),
                );
            this.shouldDisplayAddToCartCTA$ = combineLatest([
                this.shouldDisplayNotifyMeCTA$,
                this.shouldDisplayRegisterYourInterestCTA$,
            ]).pipe(
                map(
                    ([
                        shouldDisplayNotifyMeCTA,
                        shouldDisplayRegisterYourInterestCTA,
                    ]) =>
                        !shouldDisplayNotifyMeCTA &&
                        !shouldDisplayRegisterYourInterestCTA &&
                        !(
                            [
                                ProductTemplateType.AppleWatch,
                                ProductTemplateType.Beltkit,
                            ].includes(
                                this.product
                                    .templateType as ProductTemplateType,
                            ) &&
                            this.product.stock &&
                            isRetailOnlyStock(this.product.stock)
                        ),
                ),
            );
        }

        this.shouldDisplayApplePayCTA$ = this.showApplePayButton
            ? combineLatest([
                  this.paymentMethodsFacade.isApplePayToDisplay(
                      ApplePayPage.PRODUCT_PAGE,
                      [this.product],
                  ),
                  this.shouldDisplayNotifyMeCTA$ ?? of(false),
                  this.shouldDisplayRegisterYourInterestCTA$ ?? of(false),
              ]).pipe(
                  map(
                      ([
                          shouldDisplayApplePay,
                          shouldDisplayNotifyMe,
                          shouldDisplayRYI,
                      ]) =>
                          shouldDisplayApplePay &&
                          !shouldDisplayNotifyMe &&
                          !shouldDisplayRYI,
                  ),
              )
            : of(false);
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    private askForBackInStockSubscription(): void {
        this.trayFacade.openTray({
            hasOverlay: true,
            hasMapOverlay: false,
            hasCategoryInstitutional: false,
            isOpen: true,
            position: "right",
            titleKey: "hermes.back-in-stock.tray.subscription.title",
            uuid: v4(),
            name: "back-in-stock-tray",
            metadata: {},
            backgroundColor: exportedSass.secondaryColors["color-secondary"],
            closeCallback: () => {
                this.backInStockNotifyMeButton?.nativeElement.focus();
                this.backInStockFacade.resetBackInStock();
            },
        });
    }

    private addingProductToCartOnApplePayButtonClick(): Observable<void> {
        return this.applePayFacade.status$.pipe(
            filter((status) => status === ApplepayStatus.AddingProductToCart),
            switchMap(() => this.resetBasketAndEmitAddToCart()),
            map((result) => {
                if ("hasError" in result) {
                    this.applePayFacade.abort();
                    this.basketFacade.clearData();
                } else {
                    this.applePayFacade.productAddedToCart();
                }
            }),
        );
    }

    private resetBasketAndEmitAddToCart(): Observable<
        SerializedBasket | { hasError: boolean; ecomError: EcomError }
    > {
        return this.basketFacade.cart$.pipe(
            // Take current cart
            filter((cart) => !!cart),
            take(1),

            // Remove everything from it if not empty
            tap((cart) => {
                if (!this.isBasketEmpty(cart)) {
                    this.basketFacade.resetCart();
                }
            }),

            // Wait for empty basket return (or error)
            switchMap(() =>
                race(
                    this.basketFacade.cart$.pipe(
                        filter((cart) => this.isBasketEmpty(cart)),
                        take(1),
                    ),
                    this.basketFacade.error$.pipe(
                        filter((error) => error?.hasError),
                        take(1),
                    ),
                ),
            ),
            // Add the current item to the cart
            tap(() => this.addToCart.emit(false)),

            // Wait for addToCart (or error)
            switchMap(() =>
                race(
                    this.basketFacade.cart$.pipe(
                        filter((cart) => !this.isBasketEmpty(cart)),
                        take(1),
                    ),
                    this.basketFacade.error$.pipe(
                        filter((error) => error?.hasError),
                        take(1),
                    ),
                ),
            ),
        );
    }

    /**
     * @deprecated
     * All this controls shoulb be in server side (BCK). Not in client side.
     * Should be removed
     *
     * @param errorCode
     * @returns
     */
    private getErrorCodeFromProduct(errorCode: EcomErrorCodes): EcomErrorCodes {
        let completedErrorCode = errorCode ?? EcomErrorCodes.OUT_OF_STOCK;

        // Specific error message for sizes and giftsets.
        if (
            completedErrorCode !==
            EcomErrorCodes.ERROR_CODE_PERSONALIZATION_MIXEDITEMS
        ) {
            // For simple product with size variant : display SIZE_OUT_OF_STOCK
            if (
                this.product.templateType === ProductTemplateType.Simple &&
                hasSizeVariant(this.product)
            ) {
                completedErrorCode = EcomErrorCodes.SIZE_OUT_OF_STOCK;
            }
            // For product set : display ITEM_OUT_OF_STOCK message
            if (this.product.templateType === ProductTemplateType.Giftset) {
                completedErrorCode = EcomErrorCodes.ITEM_OUT_OF_STOCK;
            }
        }

        return completedErrorCode;
    }

    private isBasketEmpty(basket: SerializedBasket): boolean {
        return !Array.isArray(basket?.items) || basket.items.length === 0;
    }

    private askForRegisterYourInterestSubscription(): void {
        this.trayFacade.openTray({
            hasOverlay: true,
            hasMapOverlay: false,
            hasCategoryInstitutional: false,
            isOpen: true,
            position: "right",
            titleKey:
                "hermes.back-in-stock_exceptional-pieces.tray.subscription.title",
            uuid: v4(),
            name: "register-your-interest-tray",
            metadata: {},
            backgroundColor: exportedSass.secondaryColors["color-secondary"],
            openCallback: () =>
                this.registerYourInterestFacade.resetRegisterYourInterest(),
            closeCallback: () => {
                this.registerYourInterestButton?.nativeElement.focus();
            },
        });
    }
}
